import {
  type ComponentType,
  type ReactElement,
  type ReactNode,
  useEffect,
  useState,
} from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { FullPageLoader } from '@/components/ui/loading-spinner'
import { getDefaultRouteForUser } from '@/lib/router'
import { useAuthStore } from '@/stores/auth'

// Error component for authentication failures
function AuthError({
  message,
  onRetry,
}: {
  message: string
  onRetry?: () => void
}) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="max-w-md p-6 text-center space-y-4">
        <div className="text-destructive text-lg font-semibold">
          Authentication Error
        </div>
        <p className="text-muted-foreground">{message}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  )
}

interface ProtectedRouteProps {
  children: ReactNode
  roles?: string[]
  permissions?: string[]
  fallback?: ReactNode
  redirectTo?: string
  requireEmailVerification?: boolean
  requiresCdpConnection?: boolean
}

export function ProtectedRoute({
  children,
  roles = [],
  permissions = [],
  fallback,
  redirectTo = '/login',
  requireEmailVerification = false,
  requiresCdpConnection = false,
}: ProtectedRouteProps): ReactElement {
  const {
    user,
    role,
    organization,
    isAuthenticated,
    isTokenExpired,
    refreshToken,
    authState,
    error,
    isOnboardingCompleted,
  } = useAuthStore()

  const [isValidating, setIsValidating] = useState(true)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const location = useLocation()
  // Session validation effect
  useEffect(() => {
    const validateSession = async () => {
      try {
        setIsValidating(true)
        setValidationError(null)

        // If user is not authenticated, no need to validate
        if (!isAuthenticated()) {
          setIsValidating(false)
          return
        }

        // Quick check: if we have a user and token is not expired, skip validation
        if (user && !isTokenExpired()) {
          setIsValidating(false)
          return
        }

        // Check if token is expired and try to refresh
        if (isTokenExpired()) {
          const refreshSuccess = await refreshToken()

          if (!refreshSuccess) {
            setValidationError('Session expired. Please log in again.')
            setIsValidating(false)
            return
          }
        }

        setIsValidating(false)
      } catch (err) {
        console.error('Session validation error:', err)
        setValidationError('Session validation failed. Please log in again.')
        setIsValidating(false)
      }
    }

    validateSession()
  }, [isAuthenticated, isTokenExpired, refreshToken, user, retryCount])

  // Show loading spinner while validating
  if (isValidating || authState === 'loading') {
    return (
      (fallback as ReactElement) || (
        <FullPageLoader message="Checking authentication..." />
      )
    )
  }

  // Show error if validation failed
  if (validationError || (error && authState === 'error')) {
    return (
      <AuthError
        message={validationError || error?.message || 'Authentication failed'}
        onRetry={() => {
          setValidationError(null)
          setIsValidating(true)
          setRetryCount((c) => c + 1)
        }}
      />
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated() || !user) {
    return (
      <Navigate
        to={redirectTo}
        state={{
          from: { pathname: location.pathname, search: location.search },
          message: 'Please log in to access this page',
        }}
        replace
      />
    )
  }

  // Check email verification requirement
  if (requireEmailVerification && user && !user.confirmed) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="max-w-md p-6 text-center space-y-4 border rounded-lg bg-card">
          <h2 className="text-xl font-semibold">Email Verification Required</h2>
          <p className="text-muted-foreground">
            Please verify your email address to access this page. Check your
            inbox for a verification link.
          </p>
          <button
            onClick={() => {
              // TODO: Implement resend verification email
            }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Resend Verification Email
          </button>
        </div>
      </div>
    )
  }

  const isOnboardingRoute = location.pathname === '/onboarding'

  if (isOnboardingRoute) {
    // If user has already completed onboarding, redirect to dashboard
    if (isOnboardingCompleted()) {
      return (
        <Navigate
          to={getDefaultRouteForUser(user)}
          state={{
            message: 'Onboarding already completed',
          }}
          replace
        />
      )
    }
    // If not completed, allow access to onboarding
  } else {
    // For all other routes, check if onboarding is required
    if (!isOnboardingCompleted()) {
      return (
        <Navigate
          to="/onboarding"
          state={{
            from: { pathname: location.pathname, search: location.search },
            message: 'Please complete your onboarding to access this page',
          }}
          replace
        />
      )
    }
  }

  // Role-based access control (RBAC) - Future implementation
  if (roles.length > 0 && role) {
    const userRole = role.name.toLowerCase()
    const hasRequiredRole = roles.some(
      (requiredRole) => requiredRole.toLowerCase() === userRole
    )

    if (!hasRequiredRole) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="max-w-md p-6 text-center space-y-4 border rounded-lg bg-card">
            <h2 className="text-xl font-semibold text-destructive">
              Access Denied
            </h2>
            <p className="text-muted-foreground">
              You don't have permission to access this page. Required role:{' '}
              {roles.join(' or ')}.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      )
    }
  }

  // CDP connection check
  if (requiresCdpConnection && !organization?.hasCdpConnection) {
    return (
      <Navigate
        to="/dashboard"
        state={{
          message: 'A BlueConic connection is required to access this feature.',
        }}
        replace
      />
    )
  }

  // Permission-based access control - Future implementation
  if (permissions.length > 0) {
    // TODO: Implement permission checking when backend supports it
    // For now, we'll just track what permissions would be required

    // Placeholder for future permission checking
    const hasRequiredPermissions = true // This would be replaced with actual permission checking

    if (!hasRequiredPermissions) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="max-w-md p-6 text-center space-y-4 border rounded-lg bg-card">
            <h2 className="text-xl font-semibold text-destructive">
              Insufficient Permissions
            </h2>
            <p className="text-muted-foreground">
              You don't have the required permissions to access this page.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      )
    }
  }

  // All checks passed, render the protected content
  return (<>{children}</>) as ReactElement
}

// Higher-order component version for easier usage
export function withProtectedRoute<P extends object>(
  Component: ComponentType<P>,
  options?: Omit<ProtectedRouteProps, 'children'>
) {
  return function ProtectedComponent(props: P) {
    return (
      <ProtectedRoute {...options}>
        <Component {...props} />
      </ProtectedRoute>
    )
  }
}

// Hook for checking authentication status in components
export function useProtectedRoute() {
  const { user, role, isAuthenticated, authState, error } = useAuthStore()

  return {
    user,
    role,
    isAuthenticated: isAuthenticated(),
    isLoading: authState === 'loading',
    error,
    hasRole: (roleName: string) =>
      role?.name.toLowerCase() === roleName.toLowerCase(),
    hasAnyRole: (roles: string[]) =>
      roles.some(
        (roleName) => role?.name.toLowerCase() === roleName.toLowerCase()
      ),
    // TODO: Add permission checking methods when backend supports it
    hasPermission: (_permission: string) => {
      // Permission checking not yet implemented
      return true // Placeholder
    },
  }
}

// Export types for external use
export type { ProtectedRouteProps }
